import React, { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import Swal from "sweetalert2"; 
import { customFetch } from '../apiClient';
import PrescriptionPrint from "./PrescriptionPrint"; 

// 🎨 Theme Select Styles
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '26px', height: '26px', fontSize: '11px', borderRadius: '4px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    padding: '0'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px', height: '24px' }),
  indicatorsContainer: (base) => ({ ...base, height: '24px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  input: (base) => ({ ...base, margin: '0', padding: '0', fontSize: '11px' }),
  // 👈 HIGHEST Z-INDEX for Dropdown Menus
  menu: (base) => ({ ...base, zIndex: 999999, borderRadius: '4px', position: 'absolute' }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  option: (base, state) => ({
    ...base, fontSize: '11px', padding: '6px 10px', cursor: 'pointer',
    backgroundColor: state.isSelected ? 'var(--theme-color, #0088cc)' : state.isFocused ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' : 'white',
    color: state.isSelected ? 'white' : '#333',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
};

const dosageOptions = [
  { value: "1-0-1", label: "1-0-1 (BD - Twice a day)" },
  { value: "1-1-1", label: "1-1-1 (TDS - Thrice a day)" },
  { value: "1-0-0", label: "1-0-0 (OD - Morning)" },
  { value: "0-0-1", label: "0-0-1 (OD - Night)" },
  { value: "SOS", label: "SOS (As needed)" },
  { value: "STAT", label: "STAT (Immediately)" }
];
const durationOptions = [
  { value: "3 Days", label: "3 Days" },
  { value: "5 Days", label: "5 Days" },
  { value: "1 Week", label: "1 Week" },
  { value: "15 Days", label: "15 Days" },
  { value: "1 Month", label: "1 Month" }
];
const instructionOptions = [
  { value: "After Meal", label: "After Meal (खाने के बाद)" },
  { value: "Before Meal", label: "Before Meal (खाली पेट)" },
  { value: "At Bedtime", label: "At Bedtime (सोते समय)" },
  { value: "With Warm Water", label: "With Warm Water (गर्म पानी से)" }
];

const formatSimpleDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const PrescriptionHistory = () => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [isCardView, setIsCardView] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editMedicines, setEditMedicines] = useState([]);
  const [currentEditMed, setCurrentEditMed] = useState({ selectedMed: null, dosage: "", duration: "", instructions: "" });

  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
    if (hospitalId) {
      customFetch(`/Company/${hospitalId}`)
        .then(res => res.json())
        .then(data => setCompanyDetails(data))
        .catch(console.error);
    }
  }, []);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(t); }
  }, [success]);

  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(""), 4000); return () => clearTimeout(t); }
  }, [error]);

  const loadPatientOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const ipdPromise = customFetch(`/Ipds/list?search=${encodeURIComponent(inputValue)}`).then(res => res.json()).catch(() => []);
      const opdPromise = customFetch(`/Opds/list?search=${encodeURIComponent(inputValue)}&useDateFilter=false`).then(res => res.json()).then(d => d.data || []).catch(() => []);

      const [ipdData, opdData] = await Promise.all([ipdPromise, opdPromise]);

      const ipdOptions = (Array.isArray(ipdData) ? ipdData : []).map(item => ({ value: item.uhiD_No || item.uhidNo, originalData: item }));
      const opdOptions = (Array.isArray(opdData) ? opdData : []).map(item => ({ value: item.uhidNo || item.uhiD_No, originalData: item }));

      const allOptions = [...ipdOptions, ...opdOptions];
      const uniqueMap = new Map();
      allOptions.forEach(item => {
        if (item.value && !uniqueMap.has(item.value)) { uniqueMap.set(item.value, item); }
      });

      return Array.from(uniqueMap.values()).map(item => {
        const dateStr = item.originalData.regDate || item.originalData.ipD_Date || item.originalData.opdDate;
        return {
          label: `👤 ${item.originalData.name} | UHID: ${item.value} | Date: ${formatSimpleDate(dateStr)} | Mob: ${item.originalData.mobileNo}`,
          value: item.value,
          originalData: item.originalData
        };
      });
    } catch { return []; }
  };

  const loadMedicineOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const res = await customFetch(`/MedicineDT/search?medicine=${inputValue}`);
      const data = await res.json();
      return data.map(m => ({ label: m.medicine, value: m.medID, medName: m.medicine }));
    } catch { return []; }
  };

  const fetchHistory = async () => {
    setLoading(true); setError("");
    try {
      let url = `/Prescriptions/list?fromDate=${fromDate}&toDate=${toDate}`;
      if (selectedPatient) url += `&uhid=${selectedPatient.value}`;

      const res = await customFetch(url);
      if (!res.ok) throw new Error("Failed to fetch history.");
      
      const data = await res.json();
      setHistoryData(data);
      setCurrentPage(1); 
    } catch (err) {
      setError(err.message);
      setHistoryData([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchHistory();
  }, [fromDate, toDate, selectedPatient]);

  const handlePatientSelect = (selected) => {
    setSelectedPatient(selected);
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  // 👈 PRINT FIX: Mapping all details properly from fetched backend data
  const handleViewPrint = (record) => {
    const dataForPrint = {
      ...record,
      name: record.patientName,
      patientName: record.patientName,
      dailySrNo: record.dailySrNo ?? record.DailySrNo,
      age: record.age,
      ageIn: record.ageIn,
      gender: record.gender,
      address: record.address,
      mobileNo: record.mobileNo,
      // Vitals matching Exact properties
      BP: record.bp,
      Pulse: record.pulses,
      SPO2: record.oxy,
      Temp: record.temps,
      Weight: record.weights,
      Height: record.heights,
      RBS: record.rbs,
    };
    setPrintData(dataForPrint);
    setShowPrintModal(true);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        setActionLoading(true);
        try {
          const res = await customFetch(`/Prescriptions/${id}`, { method: "DELETE" });
          if (!res.ok) throw new Error("Failed to delete prescription.");
          Swal.fire("Deleted!", "Prescription has been deleted.", "success");
          fetchHistory();
        } catch (err) { 
          Swal.fire("Error!", err.message, "error");
        } finally { 
          setActionLoading(false); 
        }
      }
    });
  };

  const openEditModal = (record) => {
    setEditData({ ...record });
    setEditMedicines(record.medicines.map(m => ({
      medID: m.medID, medicineName: m.medicineName, dosage: m.dosage || "", duration: m.duration || "", instructions: m.instructions || ""
    })));
    setCurrentEditMed({ selectedMed: null, dosage: "", duration: "", instructions: "" });
    setShowEditModal(true);
  };

  const handleEditMedSelect = (name, opt) => { setCurrentEditMed({ ...currentEditMed, [name]: opt ? opt.value : "" }); };

  const addEditMedToCart = () => {
    if (!currentEditMed.selectedMed) return alert("Select a medicine first.");
    if (editMedicines.some(m => m.medID === currentEditMed.selectedMed.value)) return alert("Medicine already added.");
    setEditMedicines([...editMedicines, {
      medID: currentEditMed.selectedMed.value, medicineName: currentEditMed.selectedMed.medName,
      dosage: currentEditMed.dosage, duration: currentEditMed.duration, instructions: currentEditMed.instructions
    }]);
    setCurrentEditMed({ selectedMed: null, dosage: "", duration: "", instructions: "" });
  };

  const removeEditMedFromCart = (id) => { setEditMedicines(editMedicines.filter(m => m.medID !== id)); };

  const handleSaveEdit = async () => {
    if (editMedicines.length === 0) return alert("Please add at least one medicine.");
    setActionLoading(true);
    try {
      const payload = {
        uhidNo: editData.uhidNo, visitType: editData.visitType, visitId: editData.visitId, visitSrNo: editData.visitSrNo, doctorId: 1,
        chiefComplaints: editData.chiefComplaints, diagnosis: editData.diagnosis, notes: editData.notes,
        medicines: editMedicines.map(m => ({ medID: m.medID, dosage: m.dosage, duration: m.duration, instructions: m.instructions }))
      };
      const res = await customFetch(`/Prescriptions/${editData.prescriptionId}`, { method: "PUT", body: JSON.stringify(payload) });
      if (!res.ok) throw new Error("Failed to update prescription.");
      setSuccess("Prescription updated successfully.");
      setShowEditModal(false);
      fetchHistory();
    } catch (err) { setError(err.message); } finally { setActionLoading(false); }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = historyData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(historyData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="dc-wrap">
      <div className="dc-card">
        
        <div className="dc-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div className="dc-header-brand">
            <div className="dc-header-icon"><i className="bi bi-clock-history"></i></div>
            <span className="dc-header-title">Prescription History & Reports</span>
          </div>
          <div className="view-toggle">
            <span className={!isCardView ? "active" : ""} onClick={() => setIsCardView(false)}>
              <i className="bi bi-table"></i> Table
            </span>
            <span className={isCardView ? "active" : ""} onClick={() => setIsCardView(true)}>
              <i className="bi bi-grid-fill"></i> Cards
            </span>
          </div>
        </div>

        <div className="dc-body">
          {error && <div className="dc-alert dc-alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
          {success && <div className="dc-alert dc-alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

          <div className="dc-section">
            <div className="dc-section-title"><i className="bi bi-search"></i><span>Search Filters</span></div>
            <div className="dc-section-body">
              <div className="dc-grid dc-grid-row-filters dc-mb2">
                <div>
                  <label className="dc-label">From Date</label>
                  <input type="date" className="dc-input" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                </div>
                <div>
                  <label className="dc-label">To Date</label>
                  <input type="date" className="dc-input" value={toDate} onChange={e => setToDate(e.target.value)} />
                </div>
                <div>
                  <label className="dc-label">Filter By Patient (Optional)</label>
                  <AsyncSelect
                    cacheOptions loadOptions={loadPatientOptions} onChange={handlePatientSelect} value={selectedPatient}
                    placeholder="Type Name, UHID..." isClearable styles={selectStyles} classNamePrefix="react-select"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button type="button" className="dc-btn-submit" onClick={fetchHistory} style={{ width: '100%' }}>
                    <i className="bi bi-arrow-clockwise"></i> Load Data
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="dc-section" style={{ marginTop: '15px' }}>
            <div className="dc-section-title"><i className="bi bi-journal-medical"></i><span>Prescription Records</span></div>
            <div className="dc-section-body" style={{ padding: isCardView ? '15px' : '0' }}>
              
              {loading ? (
                <div style={{ textAlign: 'center', padding: '30px' }}><span className="spinner-border spinner-border-sm me-2 text-primary"></span> Loading data...</div>
              ) : historyData.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#a0aec0', padding: '30px' }}>No records found for the selected criteria.</div>
              ) : isCardView ? (
                
                <div className="cards-container">
                  {currentItems.map((record) => (
                    <div className="theme-glass-card" key={record.prescriptionId}>
                      <div className="glass-header">
                        <div className="glass-p-info">
  <div className="g-name">{record.patientName}</div>
  <div className="g-uhid">
    UHID: {record.uhidNo}
    {/* 👇 DSR Badge for Card View 👇 */}
    {(record.dailySrNo || record.DailySrNo) ? (
      <span style={{ 
        marginLeft: '8px', 
        padding: '1px 5px', 
        background: 'rgba(255,255,255,0.4)', 
        borderRadius: '4px', 
        border: '1px solid rgba(0,0,0,0.1)',
        fontWeight: 'bold',
        color: '#0f172a'
      }}>
        DSR: {record.dailySrNo || record.DailySrNo}
      </span>
    ) : null}
  </div>
</div>
                        <div className="g-type" data-type={record.visitType}>{record.visitType}</div>
                      </div>
                      
                      <div className="glass-body">
                        <div className="g-meta">
                          <span><i className="bi bi-calendar3"></i> {formatSimpleDate(record.prescriptionDate)}</span>
                          <span><i className="bi bi-person-badge"></i> {record.doctorName}</span>
                        </div>
                        
                        <div className="g-content">
                          {record.chiefComplaints && <div className="g-row"><strong>C/O:</strong> {record.chiefComplaints}</div>}
                          {record.diagnosis && <div className="g-row"><strong>Dx:</strong> {record.diagnosis}</div>}
                          {record.notes && <div className="g-row"><strong>Notes:</strong> {record.notes}</div>}
                        </div>

                        <div className="g-meds-area">
                          <div className="g-meds-title">Medicines Prescribed:</div>
                          {record.medicines && record.medicines.length > 0 ? (
                             <ul className="g-med-list">
                               {record.medicines.map((med, midx) => (
                                 <li key={midx}>
                                   <strong>{med.medicineName}</strong> - {med.dosage} <em>({med.duration})</em>
                                 </li>
                               ))}
                             </ul>
                          ) : <span style={{fontSize:'10px', color:'#64748b'}}>No medicines added</span>}
                        </div>
                      </div>

                      <div className="glass-footer">
                        <button className="g-btn g-btn-print" onClick={() => handleViewPrint(record)}><i className="bi bi-printer"></i></button>
                        <button className="g-btn g-btn-edit" onClick={() => openEditModal(record)}><i className="bi bi-pencil-square"></i></button>
                        <button className="g-btn g-btn-del" onClick={() => handleDelete(record.prescriptionId)} disabled={actionLoading}><i className="bi bi-trash"></i></button>
                      </div>
                    </div>
                  ))}
                </div>

              ) : (

                <div className="dc-table-wrapper" style={{ border: 'none', borderRadius: '0 0 5px 5px' }}>
                  <table className="dc-table dc-table-expandable">
                    <thead>
                      <tr>
                        <th width="5%"></th>
                        <th width="12%">Date</th>
                        <th width="20%">Patient Name</th>
                        <th width="15%">UHID</th>
                        <th width="12%">Type</th>
                        <th width="20%">Doctor</th>
                        <th width="16%" style={{ textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((record) => {
                        const isExpanded = expandedRows.has(record.prescriptionId);
                        return (
                          <React.Fragment key={record.prescriptionId}>
                            <tr className={isExpanded ? "row-expanded-main" : ""}>
                              <td style={{ textAlign: 'center' }}>
                                <button className="btn-expand" onClick={() => toggleRow(record.prescriptionId)}>
                                  <i className={`bi ${isExpanded ? 'bi-dash-circle-fill text-danger' : 'bi-plus-circle-fill text-success'}`}></i>
                                </button>
                              </td>
                              <td style={{ fontWeight: 600 }}>{formatSimpleDate(record.prescriptionDate)}</td>
                              <td style={{ fontWeight: 600, color: 'var(--theme-color, #0088cc)' }}>{record.patientName}</td>
                              <td>{record.uhidNo}</td>
                             <td>
  <span className={`badge-type badge-${record.visitType.toLowerCase()}`}>{record.visitType}</span>
  {/* 👇 DSR Badge for Table View 👇 */}
  {(record.dailySrNo || record.DailySrNo) ? (
    <span style={{ 
      marginLeft: '6px', 
      padding: '2px 6px', 
      background: '#f8fafc', 
      border: '1px solid #cbd5e0', 
      borderRadius: '4px', 
      fontSize: '9px', 
      fontWeight: 'bold', 
      color: '#475569' 
    }}>
      DSR: {record.dailySrNo || record.DailySrNo}
    </span>
  ) : null}
</td>
                              <td>{record.doctorName}</td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                  <button type="button" className="dc-btn-action btn-print" onClick={() => handleViewPrint(record)} title="Print"><i className="bi bi-printer"></i></button>
                                  <button type="button" className="dc-btn-action btn-edit" onClick={() => openEditModal(record)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                                  <button type="button" className="dc-btn-action btn-delete" onClick={() => handleDelete(record.prescriptionId)} disabled={actionLoading} title="Delete"><i className="bi bi-trash"></i></button>
                                </div>
                              </td>
                            </tr>
                            
                            {isExpanded && (
                              <tr className="row-expanded-content">
                                <td colSpan="7">
                                  <div className="expand-container">
                                    <div className="expand-clinical">
                                      {record.chiefComplaints && <div><strong>C/O:</strong> {record.chiefComplaints}</div>}
                                      {record.diagnosis && <div><strong>Diagnosis:</strong> {record.diagnosis}</div>}
                                      {record.notes && <div><strong>Prov Dx / Notes:</strong> {record.notes}</div>}
                                    </div>
                                    <div className="expand-meds">
                                      <strong>Medicines:</strong>
                                      {record.medicines && record.medicines.length > 0 ? (
                                        <table className="mini-table">
                                          <thead><tr><th>Medicine</th><th>Dosage</th><th>Duration</th><th>Instructions</th></tr></thead>
                                          <tbody>
                                            {record.medicines.map((m, i) => (
                                              <tr key={i}>
                                                <td>{m.medicineName}</td><td>{m.dosage}</td><td>{m.duration}</td><td>{m.instructions}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      ) : <span> No medicines prescribed.</span>}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              
              {!loading && historyData.length > 0 && (
                <div className="dc-pagination-wrapper">
                  <div className="dc-pagination-info">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, historyData.length)} of {historyData.length} entries
                  </div>
                  <div className="dc-pagination-controls">
                    <div className="per-page-select">
                      <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="dc-input" style={{ width: 'auto', display: 'inline-block', height: '24px', padding: '0 5px' }}>
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                      </select>
                    </div>
                    <div className="page-buttons">
                      <button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>Prev</button>
                      <span className="page-current">{currentPage} / {totalPages}</span>
                      <button disabled={currentPage === totalPages || totalPages === 0} onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {showPrintModal && printData && companyDetails && (
        <PrescriptionPrint show={showPrintModal} data={printData} companyDetails={companyDetails} onClose={() => setShowPrintModal(false)} />
      )}

      {/* ✏️ EDIT MODAL FIX: overflow visible is applied deeply */}
      {showEditModal && editData && (
        <div className="dc-modal-overlay">
          <div className="dc-modal-content">
            <div className="dc-modal-header">
              <h5><i className="bi bi-pencil-square"></i> Edit Prescription</h5>
              <button className="dc-btn-close" onClick={() => setShowEditModal(false)}><i className="bi bi-x-lg"></i></button>
            </div>
            
            <div className="dc-modal-body">
              <div className="dc-grid dc-grid-row-clinical dc-mb2">
                <div><label className="dc-label">Chief Complaints</label><textarea className="dc-textarea" value={editData.chiefComplaints || ""} onChange={e => setEditData({...editData, chiefComplaints: e.target.value})} /></div>
                <div><label className="dc-label">Prov Dx</label><textarea className="dc-textarea" value={editData.notes || ""} onChange={e => setEditData({...editData, notes: e.target.value})} /></div>
                <div><label className="dc-label">Diagnosis</label><textarea className="dc-textarea" value={editData.diagnosis || ""} onChange={e => setEditData({...editData, diagnosis: e.target.value})} /></div>
              </div>

              <div style={{ marginTop: '15px' }}>
                <label className="dc-label" style={{ fontSize: '12px', borderBottom: '1px solid #ccc', paddingBottom: '4px' }}>Medicines</label>
                <div className="dc-grid dc-grid-row-medicine align-items-end" style={{ marginTop: '10px' }}>
                  
                  <div>
                    <label className="dc-label">Search Medicine</label>
                    <AsyncSelect 
                      cacheOptions 
                      loadOptions={loadMedicineOptions} 
                      value={currentEditMed.selectedMed} 
                      onChange={val => setCurrentEditMed({...currentEditMed, selectedMed: val})} 
                      placeholder="Type Medicine..."
                      isClearable 
                      styles={selectStyles} 
                      menuPortalTarget={document.body} 
                      menuPosition="fixed"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div>
                    <label className="dc-label">Dosage</label>
                    <CreatableSelect 
                      options={dosageOptions} 
                      value={currentEditMed.dosage ? {label: currentEditMed.dosage, value: currentEditMed.dosage} : null} 
                      onChange={val => handleEditMedSelect('dosage', val)} 
                      placeholder="Select/Type..."
                      isClearable 
                      styles={selectStyles} 
                      menuPortalTarget={document.body} 
                      menuPosition="fixed"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div>
                    <label className="dc-label">Duration</label>
                    <CreatableSelect 
                      options={durationOptions} 
                      value={currentEditMed.duration ? {label: currentEditMed.duration, value: currentEditMed.duration} : null} 
                      onChange={val => handleEditMedSelect('duration', val)} 
                      placeholder="Select/Type..."
                      isClearable 
                      styles={selectStyles} 
                      menuPortalTarget={document.body} 
                      menuPosition="fixed"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div>
                    <label className="dc-label">Instructions</label>
                    <CreatableSelect 
                      options={instructionOptions} 
                      value={currentEditMed.instructions ? {label: currentEditMed.instructions, value: currentEditMed.instructions} : null} 
                      onChange={val => handleEditMedSelect('instructions', val)} 
                      placeholder="Select/Type..."
                      isClearable 
                      styles={selectStyles} 
                      menuPortalTarget={document.body} 
                      menuPosition="fixed"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div>
                    <button type="button" className="dc-btn-add" onClick={addEditMedToCart} disabled={!currentEditMed.selectedMed}>
                      <i className="bi bi-plus-lg"></i> Add
                    </button>
                  </div>

                </div>

                <div className="dc-table-wrapper" style={{ marginTop: '10px', border: '1px solid #e0e6ed', maxHeight: '150px', overflowY: 'auto' }}>
                  <table className="dc-table">
                    <thead><tr style={{position: 'sticky', top: 0, zIndex: 10}}><th width="5%">#</th><th width="35%">Medicine</th><th width="15%">Dosage</th><th width="15%">Duration</th><th width="20%">Instructions</th><th width="10%" style={{ textAlign: 'center' }}>Action</th></tr></thead>
                    <tbody>
                      {editMedicines.length === 0 ? (
                        <tr><td colSpan="6" style={{ textAlign: 'center', color: '#a0aec0', padding: '15px 0' }}>No medicines added.</td></tr>
                      ) : (
                        editMedicines.map((med, idx) => (
                          <tr key={med.medID}>
                            <td>{idx + 1}</td>
                            <td style={{ fontWeight: 600 }}>{med.medicineName}</td>
                            <td>{med.dosage}</td>
                            <td>{med.duration}</td>
                            <td>{med.instructions}</td>
                            <td style={{ textAlign: 'center' }}><button type="button" className="dc-btn-icon-danger" onClick={() => removeEditMedFromCart(med.medID)}><i className="bi bi-trash"></i></button></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="dc-modal-footer">
              <button className="dc-btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="dc-btn-submit" onClick={handleSaveEdit} disabled={actionLoading}>{actionLoading ? "Saving..." : "Save Changes"}</button>
            </div>
          </div>
        </div>
      )}

      {/* 🎨 STYLES */}
      <style jsx>{`
        *, *::before, *::after { box-sizing: border-box; }
        .dc-wrap { padding: 0; font-family: system-ui, sans-serif; }
        .dc-card { background: #fff; border-radius: 6px; box-shadow: 0 1px 8px rgba(0,0,0,0.06); border: 1px solid #e0e6ed; }
        .dc-header { background: var(--theme-color, #0088cc); padding: 6px 12px; border-radius: 6px 6px 0 0; border-bottom: 2px solid rgba(0,0,0,0.1); }
        .dc-header-inner { display: flex; align-items: center; }
        .dc-header-brand { display: flex; align-items: center; gap: 6px; color: white; font-weight: 600; font-size: 13px; }
        .dc-body { padding: 10px 12px; }
        
        .view-toggle { display: flex; background: rgba(0,0,0,0.2); border-radius: 4px; overflow: hidden; }
        .view-toggle span { padding: 4px 12px; font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.7); cursor: pointer; transition: 0.2s; display: flex; gap: 5px; align-items: center; }
        .view-toggle span.active { background: white; color: var(--theme-color, #0088cc); }
        .view-toggle span:hover:not(.active) { color: white; background: rgba(0,0,0,0.3); }

        .dc-section { background: #f8f9fa; border: 1px solid #e0e6ed; border-radius: 5px; overflow: visible; }
        .dc-section-title { background: var(--theme-color, #0088cc); padding: 5px 10px; display: flex; align-items: center; gap: 5px; border-radius: 4px 4px 0 0; color: white; font-size: 11px; font-weight: 600; }
        .dc-section-body { padding: 10px; background: #fff; border-radius: 0 0 5px 5px; }
        
        .dc-grid { display: grid; gap: 8px; align-items: start; }
        .dc-grid-row-filters { grid-template-columns: 1fr 1fr 2fr 100px; }
        .dc-grid-row-patient { grid-template-columns: 2fr 1fr; }
        .dc-grid-row-clinical { grid-template-columns: repeat(3, 1fr); }
        .dc-grid-row-medicine { grid-template-columns: 3fr 1.5fr 1.5fr 2fr 80px; align-items: end; }
        .dc-mb2 { margin-bottom: 0px; }

        .dc-label { display: block; font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 4px; }
        .dc-required { color: #dc2626; }
        .dc-input { width: 100%; height: 26px; padding: 2px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; outline: none; }
        .dc-input:focus { border-color: var(--theme-color, #0088cc); }
        .dc-textarea { width: 100%; min-height: 45px; padding: 4px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; outline: none; resize: vertical; }
        .dc-textarea:focus { border-color: var(--theme-color, #0088cc); }
        
        .dc-table-wrapper { width: 100%; overflow-x: auto; }
        .dc-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .dc-table th { background: #f1f5f9; padding: 8px 10px; text-align: left; font-weight: 600; color: #475569; border-bottom: 1px solid #e0e6ed; white-space: nowrap; }
        .dc-table td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; }
        .dc-table tbody tr:hover { background: #f8fafc; }
        
        .btn-expand { background: none; border: none; font-size: 14px; cursor: pointer; padding: 0; outline: none; }
        .row-expanded-main { background: #f8fafc; border-left: 3px solid var(--theme-color, #0088cc); }
        .row-expanded-content { background: color-mix(in srgb, var(--theme-color, #0088cc) 5%, transparent); }
        .row-expanded-content > td { padding: 10px 15px !important; border-bottom: 2px solid #e2e8f0; }
        
        .expand-container { display: flex; gap: 20px; flex-wrap: wrap; }
        .expand-clinical { flex: 1; min-width: 200px; display: flex; flex-direction: column; gap: 6px; font-size: 11px; color: #475569; }
        .expand-clinical strong { color: #0f172a; }
        .expand-meds { flex: 2; min-width: 300px; }
        .expand-meds strong { display: block; margin-bottom: 5px; color: #0f172a; }
        
        .mini-table { width: 100%; border-collapse: collapse; font-size: 10px; background: white; border: 1px solid #cbd5e0; border-radius: 4px; overflow: hidden; }
        .mini-table th { background: #e2e8f0; padding: 4px 6px; text-align: left; font-weight: 600; color: #334155; }
        .mini-table td { padding: 4px 6px; border-bottom: 1px solid #e2e8f0; color: #475569; }
        
        .badge-type { padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold; }
        .badge-opd { background: #dcfce7; color: #166534; }
        .badge-ipd { background: #e0e7ff; color: #3730a3; }

        .dc-btn-action { border: 1px solid transparent; border-radius: 4px; padding: 3px 6px; font-size: 11px; cursor: pointer; transition: 0.2s; display: inline-flex; align-items: center; gap: 4px; }
        .btn-print { background: #e0f2fe; color: #0284c7; border-color: #bae6fd; }
        .btn-print:hover { background: #bae6fd; }
        .btn-edit { background: #fef08a; color: #b45309; border-color: #fde047; }
        .btn-edit:hover { background: #fde047; }
        .btn-delete { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
        .btn-delete:hover { background: #fecaca; }
        .btn-delete:disabled { opacity: 0.5; cursor: not-allowed; }

        .dc-btn-add { height: 26px; width: 100%; background: #10b981; color: white; border: none; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 4px; }
        .dc-btn-add:hover:not(:disabled) { opacity: 0.9; }
        .dc-btn-icon-danger { background: transparent; border: none; color: #ef4444; cursor: pointer; padding: 2px 4px; border-radius: 3px; }
        .dc-btn-icon-danger:hover { background: #fee2e2; }

        .dc-btn-submit { padding: 0 15px; height: 26px; background: var(--theme-color, #0088cc); border: none; border-radius: 4px; font-size: 11px; font-weight: 600; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 5px; }

        /* 👈 EDIT MODAL FIX: Using overflow visible on content/body, allowing modal wrapper to handle outer scroll if needed */
        .dc-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: flex-start; justify-content: center; z-index: 1050; backdrop-filter: blur(2px); overflow-y: auto; padding: 40px 0; }
        .dc-modal-content { background: #fff; width: 800px; max-width: 95%; border-radius: 6px; box-shadow: 0 4px 15px rgba(0,0,0,0.2); overflow: visible !important; display: flex; flex-direction: column; position: relative; margin: auto;}
        .dc-modal-header { background: var(--theme-color, #0088cc); padding: 10px 15px; color: white; display: flex; justify-content: space-between; align-items: center; border-radius: 6px 6px 0 0; }
        .dc-modal-header h5 { margin: 0; font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 6px; }
        .dc-btn-close { background: none; border: none; color: white; font-size: 14px; cursor: pointer; }
        .dc-btn-close:hover { color: #fca5a5; }
        .dc-modal-body { padding: 15px; overflow: visible !important; }
        .dc-modal-footer { padding: 10px 15px; background: #f8f9fa; border-top: 1px solid #e0e6ed; display: flex; justify-content: flex-end; gap: 10px; border-radius: 0 0 6px 6px; }
        .dc-btn-cancel { padding: 6px 15px; background: #e2e8f0; border: none; border-radius: 4px; font-size: 11px; font-weight: 600; color: #475569; cursor: pointer; }
        .dc-btn-cancel:hover { background: #cbd5e1; }

        /* Glassmorphism Cards Theme */
        .cards-container { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 15px; padding-bottom: 15px; }
        .theme-glass-card { background: color-mix(in srgb, var(--theme-color, #0088cc) 8%, white 90%); backdrop-filter: blur(10px); border: 1px solid color-mix(in srgb, var(--theme-color, #0088cc) 30%, transparent); border-radius: 8px; overflow: hidden; box-shadow: 0 4px 10px rgba(0,0,0,0.04); display: flex; flex-direction: column; transition: transform 0.2s, box-shadow 0.2s; }
        .theme-glass-card:hover { transform: translateY(-2px); box-shadow: 0 6px 15px color-mix(in srgb, var(--theme-color, #0088cc) 15%, transparent); }
        .glass-header { background: color-mix(in srgb, var(--theme-color, #0088cc) 80%, white); padding: 8px 12px; display: flex; justify-content: space-between; align-items: center; color: white; }
        .g-name { font-weight: 700; font-size: 12px; }
        .g-uhid { font-size: 10px; opacity: 0.9; }
        .g-type { font-size: 10px; font-weight: bold; background: rgba(255,255,255,0.2); padding: 2px 6px; border-radius: 4px; }
        
        .glass-body { padding: 12px; flex: 1; font-size: 11px; color: #334155; }
        .g-meta { display: flex; justify-content: space-between; border-bottom: 1px dashed color-mix(in srgb, var(--theme-color, #0088cc) 40%, transparent); padding-bottom: 6px; margin-bottom: 8px; color: var(--theme-color, #0088cc); font-weight: 600; }
        .g-row { margin-bottom: 4px; line-height: 1.4; }
        
        .g-meds-area { margin-top: 10px; background: rgba(255,255,255,0.6); border-radius: 4px; padding: 8px; border: 1px solid color-mix(in srgb, var(--theme-color, #0088cc) 15%, transparent); }
        .g-meds-title { font-weight: 600; color: #0f172a; margin-bottom: 4px; font-size: 10.5px; }
        .g-med-list { list-style: none; padding: 0; margin: 0; }
        .g-med-list li { margin-bottom: 3px; border-bottom: 1px solid #e2e8f0; padding-bottom: 3px; }
        .g-med-list li:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
        
        .glass-footer { display: flex; background: rgba(255,255,255,0.8); border-top: 1px solid color-mix(in srgb, var(--theme-color, #0088cc) 20%, transparent); }
        .g-btn { flex: 1; padding: 6px; border: none; background: transparent; cursor: pointer; transition: 0.2s; font-size: 13px; }
        .g-btn:hover { background: color-mix(in srgb, var(--theme-color, #0088cc) 10%, transparent); }
        .g-btn-print { color: #0284c7; } .g-btn-edit { color: #b45309; } .g-btn-del { color: #dc2626; }

        .dc-pagination-wrapper { display: flex; justify-content: space-between; align-items: center; padding: 10px 15px; border-top: 1px solid #e0e6ed; background: #f8fafc; border-radius: 0 0 5px 5px; margin-top: -1px; }
        .dc-pagination-info { font-size: 11px; color: #64748b; font-weight: 500; }
        .dc-pagination-controls { display: flex; align-items: center; gap: 15px; }
        .page-buttons { display: flex; align-items: center; gap: 8px; }
        .page-buttons button { background: white; border: 1px solid #cbd5e0; padding: 4px 10px; border-radius: 4px; font-size: 11px; color: #334155; cursor: pointer; transition: 0.2s; font-weight: 500; }
        .page-buttons button:hover:not(:disabled) { background: #e2e8f0; }
        .page-buttons button:disabled { opacity: 0.5; cursor: not-allowed; }
        .page-current { font-size: 11px; font-weight: 600; color: var(--theme-color, #0088cc); }

        @media (max-width: 900px) {
          .dc-grid-row-filters { grid-template-columns: 1fr 1fr; }
          .dc-grid-row-patient, .dc-grid-row-clinical { grid-template-columns: 1fr; }
          .dc-grid-row-medicine { grid-template-columns: 1fr 1fr; align-items: start; }
          .dc-grid-row-medicine > div:nth-child(1), .dc-grid-row-medicine > div:nth-child(5) { grid-column: span 2; }
        }
        @media (max-width: 600px) {
          .dc-pagination-wrapper { flex-direction: column; gap: 10px; }
        }
      `}</style>
    </div>
  );
};

export default PrescriptionHistory;